// Card Templates Library - JSON-driven card configurations
// Enhanced with nested component actions and validations support
// Created: 2026-01-05
// Updated: 2026-01-09 - Added professional card types with action/validation support

/**
 * Notes on nested components with actions/validations:
 * - Each nested component can have its own actions[] and validations[] arrays
 * - Actions use ActionConfigPopup system (onClick, onHover, etc.)
 * - Validations use ValidationModal system (date, device, business rules, etc.)
 * - Images support AssetPicker integration
 * - Nested component structure: { id, type, content, src, actions, validations, style }
 */

const nowIso = () => new Date().toISOString();

// Helper: Create a nested component with action/validation support
const createNestedComponent = (type, config = {}) => ({
  id: config.id || `nested_${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
  type,
  content: config.content || '',
  src: type === 'image' ? (config.src || 'https://via.placeholder.com/600x300') : undefined,
  actions: config.actions || [],
  validations: config.validations || [],
  style: config.style || {},
  ...config
});

const baseCard = (overrides = {}) => ({
  type: 'card',
  name: 'Card Template',
  description: '',
  commonAttrs: {
    width: '100',
    height: 'auto',
    paddingTop: '16',
    paddingBottom: '16',
    paddingLeft: '16',
    paddingRight: '16',
    marginTop: '8',
    marginBottom: '8',
    marginLeft: '0',
    marginRight: '0'
  },
  specificAttrs: {
    title: 'Card Title',
    content: 'Card content goes here',
    backgroundColor: '#ffffff',
    borderRadius: '12',
    borderWidth: '1',
    borderColor: '#E5E7EB',
    shadow: 'medium',
    nestedComponents: []
  },
  validations: [],
  actions: [],
  metadata: {
    createdAt: nowIso(),
    tags: ['card-template'],
    source: 'professional_card_templates'
  },
  ...overrides
});

export const CARD_TEMPLATES = {
  // ===========================
  // PROFESSIONAL CARD TYPES
  // ===========================

  featureCard: baseCard({
    name: 'Feature Card',
    description: 'Feature showcase with image, title, benefits list, and CTA button',
    specificAttrs: {
      title: 'Premium Features',
      content: 'Unlock the full potential of our platform',
      backgroundColor: '#ffffff',
      borderRadius: '16',
      borderWidth: '1',
      borderColor: '#E5E7EB',
      shadow: 'medium',
      nestedComponents: [
        createNestedComponent('image', {
          id: 'feature_image',
          src: 'https://via.placeholder.com/600x300',
          style: { layout: 'top', width: '100%', borderRadius: '12px 12px 0 0' }
        }),
        createNestedComponent('text', {
          id: 'benefit_1',
          content: '✓ Lightning-fast performance',
          style: { fontWeight: '500', color: '#374151', marginTop: '12px' }
        }),
        createNestedComponent('text', {
          id: 'benefit_2',
          content: '✓ Advanced analytics dashboard',
          style: { fontWeight: '500', color: '#374151', marginTop: '8px' }
        }),
        createNestedComponent('text', {
          id: 'benefit_3',
          content: '✓ 24/7 dedicated support',
          style: { fontWeight: '500', color: '#374151', marginTop: '8px' }
        }),
        createNestedComponent('button', {
          id: 'learn_more_btn',
          content: 'Learn More',
          style: { marginTop: '16px' }
        })
      ]
    }
  }),

  pricingCard: baseCard({
    name: 'Pricing Card',
    description: 'Pricing plan with icon/logo, title, highlighted price, feature list, and subscribe button',
    specificAttrs: {
      title: 'Professional Plan',
      content: 'Perfect for growing teams',
      backgroundColor: '#ffffff',
      borderRadius: '20',
      borderWidth: '2',
      borderColor: '#8B5CF6',
      shadow: 'large',
      nestedComponents: [
        createNestedComponent('text', {
          id: 'plan_icon',
          content: '🚀',
          style: { fontSize: '48px', textAlign: 'center', marginBottom: '12px' }
        }),
        createNestedComponent('text', {
          id: 'price_highlight',
          content: '$49/month',
          style: { fontSize: '32px', fontWeight: '700', color: '#8B5CF6', textAlign: 'center' }
        }),
        createNestedComponent('text', {
          id: 'price_subtitle',
          content: 'Billed annually',
          style: { fontSize: '14px', color: '#6B7280', textAlign: 'center', marginBottom: '16px' }
        }),
        createNestedComponent('text', {
          id: 'feature_1',
          content: '✓ Unlimited projects',
          style: { marginTop: '8px' }
        }),
        createNestedComponent('text', {
          id: 'feature_2',
          content: '✓ 100GB storage',
          style: { marginTop: '8px' }
        }),
        createNestedComponent('text', {
          id: 'feature_3',
          content: '✓ Priority support',
          style: { marginTop: '8px' }
        }),
        createNestedComponent('text', {
          id: 'feature_4',
          content: '✓ Advanced analytics',
          style: { marginTop: '8px' }
        }),
        createNestedComponent('button', {
          id: 'subscribe_btn',
          content: 'Subscribe Now',
          style: { marginTop: '20px', width: '100%' }
        })
      ]
    }
  }),

  testimonialCard: baseCard({
    name: 'Testimonial Card',
    description: 'Customer testimonial with avatar, quote, author details, and star rating',
    specificAttrs: {
      title: '',
      content: '"This platform has completely transformed how we manage our projects. The results speak for themselves!"',
      backgroundColor: '#F9FAFB',
      borderRadius: '16',
      borderWidth: '1',
      borderColor: '#E5E7EB',
      shadow: 'medium',
      nestedComponents: [
        createNestedComponent('image', {
          id: 'avatar_image',
          src: 'https://via.placeholder.com/80x80',
          style: { width: '80px', height: '80px', borderRadius: '50%', margin: '0 auto 16px' }
        }),
        createNestedComponent('text', {
          id: 'author_name',
          content: 'Sarah Johnson',
          style: { fontWeight: '600', fontSize: '16px', textAlign: 'center' }
        }),
        createNestedComponent('text', {
          id: 'author_role',
          content: 'CEO, TechCorp Inc.',
          style: { color: '#6B7280', fontSize: '14px', textAlign: 'center', marginTop: '4px' }
        }),
        createNestedComponent('text', {
          id: 'star_rating',
          content: '⭐⭐⭐⭐⭐',
          style: { textAlign: 'center', marginTop: '12px', fontSize: '18px' }
        })
      ]
    }
  }),

  teamMemberCard: baseCard({
    name: 'Team Member Card',
    description: 'Team profile with photo, name, job title, bio, and social links',
    specificAttrs: {
      title: '',
      content: '',
      backgroundColor: '#ffffff',
      borderRadius: '16',
      borderWidth: '1',
      borderColor: '#E5E7EB',
      shadow: 'medium',
      nestedComponents: [
        createNestedComponent('image', {
          id: 'profile_photo',
          src: 'https://via.placeholder.com/200x200',
          style: { width: '100%', borderRadius: '16px 16px 0 0' }
        }),
        createNestedComponent('text', {
          id: 'member_name',
          content: 'Alex Martinez',
          style: { fontWeight: '700', fontSize: '20px', marginTop: '16px' }
        }),
        createNestedComponent('text', {
          id: 'job_title',
          content: 'Senior Product Designer',
          style: { color: '#8B5CF6', fontWeight: '600', fontSize: '14px', marginTop: '4px' }
        }),
        createNestedComponent('text', {
          id: 'short_bio',
          content: 'Passionate about creating intuitive user experiences. 8+ years in digital design.',
          style: { color: '#6B7280', fontSize: '14px', marginTop: '12px', lineHeight: '1.5' }
        }),
        createNestedComponent('text', {
          id: 'social_links',
          content: '🔗 LinkedIn  •  🐦 Twitter  •  📧 Email',
          style: { fontSize: '14px', marginTop: '16px', textAlign: 'center' }
        })
      ]
    }
  }),

  blogPostCard: baseCard({
    name: 'Blog Post Card',
    description: 'Blog article preview with featured image, title link, excerpt, read more button, and date',
    specificAttrs: {
      title: '10 Tips for Better UI Design',
      content: 'Learn the essential principles that will transform your design workflow and create better user experiences.',
      backgroundColor: '#ffffff',
      borderRadius: '16',
      borderWidth: '1',
      borderColor: '#E5E7EB',
      shadow: 'medium',
      nestedComponents: [
        createNestedComponent('image', {
          id: 'featured_image',
          src: 'https://via.placeholder.com/600x300',
          style: { width: '100%', borderRadius: '16px 16px 0 0' }
        }),
        createNestedComponent('text', {
          id: 'post_date',
          content: 'January 9, 2026  •  5 min read',
          style: { fontSize: '12px', color: '#6B7280', marginTop: '12px' }
        }),
        createNestedComponent('text', {
          id: 'category_tag',
          content: '🎨 Design',
          style: { 
            fontSize: '12px', 
            color: '#8B5CF6', 
            backgroundColor: '#F3E8FF',
            padding: '4px 12px',
            borderRadius: '12px',
            display: 'inline-block',
            marginTop: '8px'
          }
        }),
        createNestedComponent('button', {
          id: 'read_more_btn',
          content: 'Read Article →',
          style: { marginTop: '16px' }
        })
      ]
    }
  }),

  productCard: baseCard({
    name: 'Product Card',
    description: 'E-commerce product with image, name, price badge, description, add to cart button, and rating',
    specificAttrs: {
      title: 'Premium Wireless Headphones',
      content: 'Experience crystal-clear audio with our latest noise-cancelling technology',
      backgroundColor: '#ffffff',
      borderRadius: '16',
      borderWidth: '1',
      borderColor: '#E5E7EB',
      shadow: 'medium',
      nestedComponents: [
        createNestedComponent('image', {
          id: 'product_image',
          src: 'https://via.placeholder.com/400x400',
          style: { width: '100%', borderRadius: '16px 16px 0 0' }
        }),
        createNestedComponent('text', {
          id: 'price_badge',
          content: '$299.99',
          style: { 
            fontSize: '28px', 
            fontWeight: '700', 
            color: '#10B981',
            marginTop: '12px'
          }
        }),
        createNestedComponent('text', {
          id: 'original_price',
          content: 'Was: $399.99',
          style: { 
            fontSize: '14px', 
            color: '#9CA3AF',
            textDecoration: 'line-through',
            marginTop: '4px'
          }
        }),
        createNestedComponent('text', {
          id: 'rating_stars',
          content: '⭐⭐⭐⭐⭐ (4.8/5.0)',
          style: { fontSize: '14px', marginTop: '8px' }
        }),
        createNestedComponent('text', {
          id: 'stock_status',
          content: '✓ In Stock - Ships in 24hrs',
          style: { fontSize: '12px', color: '#059669', marginTop: '8px' }
        }),
        createNestedComponent('button', {
          id: 'add_to_cart_btn',
          content: '🛒 Add to Cart',
          style: { marginTop: '16px', width: '100%' }
        })
      ]
    }
  }),

  statCard: baseCard({
    name: 'Stat Card',
    description: 'Statistics display with large number/icon, label, subtitle, and trend arrow',
    specificAttrs: {
      title: '',
      content: '',
      backgroundColor: '#EFF6FF',
      borderRadius: '16',
      borderWidth: '2',
      borderColor: '#3B82F6',
      shadow: 'medium',
      nestedComponents: [
        createNestedComponent('text', {
          id: 'stat_icon',
          content: '📊',
          style: { fontSize: '48px', textAlign: 'center', marginBottom: '8px' }
        }),
        createNestedComponent('text', {
          id: 'large_number',
          content: '24,567',
          style: { 
            fontSize: '40px', 
            fontWeight: '700', 
            color: '#1F2937',
            textAlign: 'center'
          }
        }),
        createNestedComponent('text', {
          id: 'label_text',
          content: 'Total Users',
          style: { 
            fontSize: '16px', 
            fontWeight: '600',
            color: '#4B5563',
            textAlign: 'center',
            marginTop: '8px'
          }
        }),
        createNestedComponent('text', {
          id: 'subtitle_text',
          content: 'Last 30 days',
          style: { 
            fontSize: '14px', 
            color: '#6B7280',
            textAlign: 'center',
            marginTop: '4px'
          }
        }),
        createNestedComponent('text', {
          id: 'trend_arrow',
          content: '↑ 12.5% from last month',
          style: { 
            fontSize: '14px', 
            fontWeight: '600',
            color: '#10B981',
            textAlign: 'center',
            marginTop: '12px'
          }
        })
      ]
    }
  }),

  contactCard: baseCard({
    name: 'Contact Card',
    description: 'Contact information with icon, title, details, and link/button',
    specificAttrs: {
      title: 'Get in Touch',
      content: 'We\'re here to help with any questions',
      backgroundColor: '#ffffff',
      borderRadius: '16',
      borderWidth: '1',
      borderColor: '#E5E7EB',
      shadow: 'medium',
      nestedComponents: [
        createNestedComponent('text', {
          id: 'contact_icon',
          content: '📞',
          style: { fontSize: '48px', textAlign: 'center', marginBottom: '16px' }
        }),
        createNestedComponent('text', {
          id: 'contact_title',
          content: 'Phone Support',
          style: { fontSize: '18px', fontWeight: '600', textAlign: 'center' }
        }),
        createNestedComponent('text', {
          id: 'details_text',
          content: '+1 (555) 123-4567',
          style: { 
            fontSize: '16px', 
            color: '#3B82F6',
            textAlign: 'center',
            marginTop: '8px',
            fontWeight: '500'
          }
        }),
        createNestedComponent('text', {
          id: 'availability',
          content: 'Available Mon-Fri, 9AM-6PM EST',
          style: { 
            fontSize: '14px', 
            color: '#6B7280',
            textAlign: 'center',
            marginTop: '8px'
          }
        }),
        createNestedComponent('button', {
          id: 'contact_btn',
          content: 'Call Now',
          style: { marginTop: '16px', width: '100%' }
        })
      ]
    }
  })
};

// ===========================
// HELPER FUNCTIONS
// ===========================

// Helper: deep copy to avoid mutations (same pattern as formTemplates.js)
export const getCardTemplate = (templateKey) => {
  const rawKey = String(templateKey || '');

  // Prefer exact key match (keeps camelCase keys working)
  if (CARD_TEMPLATES[rawKey]) {
    return JSON.parse(JSON.stringify(CARD_TEMPLATES[rawKey]));
  }

  // Fallback: case-insensitive match (for safety)
  const normalized = rawKey.toLowerCase();
  const matchedKey = Object.keys(CARD_TEMPLATES).find(k => k.toLowerCase() === normalized);
  if (!matchedKey) {
    throw new Error(`Card template not found: ${templateKey}`);
  }
  return JSON.parse(JSON.stringify(CARD_TEMPLATES[matchedKey]));
};

// Helper: list for dropdowns (sorted by category)
export const getCardTemplateTypes = () => {
  return Object.entries(CARD_TEMPLATES).map(([value, t]) => ({
    value,
    label: t.name || value,
    description: t.description || ''
  }));
};

// Export createNestedComponent for use in other files if needed
export { createNestedComponent };


